import type { AxiosResponse } from 'axios';

import { reactPlugin } from '@/config/telemetry';
import type { SiteComplianceApiError } from '@/lib/siteComplianceApi';

export type SiteComplianceTelemetryEventName =
  | 'SiteComplianceCheckpointUpdated'
  | 'SiteComplianceCheckpointSeeded'
  | 'SiteComplianceCheckpointDeleted'
  | 'SiteComplianceJobCompleted'
  | 'SiteComplianceJobPollingDuration'
  | 'SiteComplianceMutationFailed';

export interface SiteComplianceTelemetryProperties {
  checkpointId?: string | null;
  durationMs?: number | null;
  evaluationId?: string | null;
  jobId?: string | null;
  latencyMs?: number | null;
  siteId?: string | null;
  status?: string | null;
  statusCode?: number | null;
}

const asHeaderValue = (value: unknown) => {
  if (typeof value === 'string') return value.trim();
  if (Array.isArray(value)) return value.find((entry) => entry)?.toString() ?? '';
  if (typeof value === 'number') return `${value}`;
  return '';
};

export const createSiteComplianceCorrelationId = () => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }

  return `sc-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

export const getSiteComplianceCorrelationId = (
  response: AxiosResponse<unknown>,
  fallback: string,
) => {
  const headers = response.headers as Record<string, unknown> & {
    get?: (name: string) => unknown;
  };
  const candidates = [
    headers.get?.('x-correlation-id'),
    headers.get?.('x-ms-correlation-request-id'),
    headers['x-correlation-id'],
    headers['X-Correlation-Id'],
    headers['x-ms-correlation-request-id'],
    headers['request-id'],
  ];
  const headerValue = candidates.map(asHeaderValue).find(Boolean);

  return headerValue || fallback;
};

export const getSiteComplianceErrorCorrelationId = (
  error: SiteComplianceApiError,
) => {
  if (error.correlationId) return error.correlationId;
  if (!error.response) return null;
  return getSiteComplianceCorrelationId(error.response, '');
};

export const getSiteComplianceErrorStatus = (error: unknown) => {
  const candidate = error as Partial<SiteComplianceApiError>;
  return candidate.response?.status ?? null;
};

export const getSiteComplianceErrorMessage = (error: unknown) => {
  const candidate = error as Partial<SiteComplianceApiError>;
  const responseData = candidate.response?.data;
  const status = candidate.response?.status;

  if (!responseData) {
    return status ? `${status}` : 'Request failed';
  }

  const nestedError =
    responseData.error && typeof responseData.error === 'object'
      ? responseData.error
      : null;

  return (
    responseData.title ??
    (typeof responseData.error === 'string' ? responseData.error : undefined) ??
    nestedError?.message ??
    responseData.detail ??
    (status ? `${status}` : 'Request failed')
  );
};

export const trackSiteComplianceEvent = (
  name: SiteComplianceTelemetryEventName,
  properties: SiteComplianceTelemetryProperties = {},
) => {
  reactPlugin.trackEvent(
    { name },
    Object.entries(properties).reduce<Record<string, string | number>>(
      (acc, [key, value]) => {
        if (value === null || value === undefined || value === '') {
          return acc;
        }

        acc[key] = typeof value === 'number' ? value : `${value}`;
        return acc;
      },
      {},
    ),
  );
};

export const attachSiteComplianceMutationErrorContext = ({
  correlationId,
  durationMs,
  error,
}: {
  correlationId: string;
  durationMs: number;
  error: SiteComplianceApiError;
}) => {
  error.correlationId = getSiteComplianceErrorCorrelationId(error) ?? correlationId;
  error.durationMs = durationMs;
  return error;
};

export const logSiteComplianceMutationFailure = ({
  checkpointId,
  error,
  eventName = 'SiteComplianceMutationFailed',
  siteId,
}: {
  checkpointId?: string | null;
  error: unknown;
  eventName?: SiteComplianceTelemetryEventName;
  siteId?: string | null;
}) => {
  const apiError = error as SiteComplianceApiError;
  const correlationId = getSiteComplianceErrorCorrelationId(apiError);
  const statusCode = getSiteComplianceErrorStatus(error);

  console.error('Site Compliance mutation failed', {
    checkpointId,
    correlationId,
    error,
    siteId,
    statusCode,
  });

  trackSiteComplianceEvent(eventName, {
    checkpointId,
    durationMs: apiError.durationMs,
    siteId,
    statusCode,
  });

  return {
    correlationId,
    message: getSiteComplianceErrorMessage(error),
    statusCode,
  };
};
